import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import imageCompression from "browser-image-compression";
import * as Slider from "@radix-ui/react-slider";
import { Avatar, AvatarImage } from "./Avatar";
import Button from "../base/Button";
import ClickableText from "../base/ClickableText";
import Spinner from "../base/Spinner";
import { UploadICon } from "../Icons/Icons";
import { useLocalizedString } from "@amzn/aws-marketing-identity-kit-localizations";
const MinusIcon = () => (_jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "12", height: "3", viewBox: "0 0 12 3", fill: "none", children: _jsx("path", { d: "M0 1.52002H12", stroke: "#161D26", strokeWidth: "2", strokeLinejoin: "round" }) }));
const PlusIcon = () => (_jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "12", height: "13", viewBox: "0 0 12 13", fill: "none", children: [_jsx("path", { d: "M0 6.52002H12", stroke: "#161D26", strokeWidth: "2", strokeLinejoin: "round" }), _jsx("path", { d: "M6 12.52V0.52002", stroke: "#161D26", strokeWidth: "2", strokeLinejoin: "round" })] }));
const ImageUploader = ({ initialImageUrl, onSave, onRemove, onCancel, maxWidthOrHeight, }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [displayedImageUrl, setDisplayedImageUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [showUploader, setShowUploader] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [fileName, setFileName] = useState(null);
    const { t } = useLocalizedString();
    useEffect(() => {
        if (initialImageUrl) {
            setImageUrl(initialImageUrl);
            setDisplayedImageUrl(initialImageUrl);
        }
    }, [initialImageUrl]);
    const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (typeof reader.result === "string") {
                    setImageUrl(reader.result);
                    setCrop({ x: 0, y: 0 });
                    setZoom(1);
                    setRotation(0);
                    setIsUploading(true);
                    setShowUploader(false);
                    setCurrentFile(file);
                    setFileName(file.name);
                }
            };
            reader.readAsDataURL(file);
        }
    }, []);
    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            "image/png": [".png"],
            "image/jpeg": [".jpg", ".jpeg"],
            "image/webp": [".webp"],
        },
        maxFiles: 1,
        maxSize: 20 * 1024 * 1024,
    });
    const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);
    const createImage = (url) => new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener("load", () => resolve(image));
        image.addEventListener("error", reject);
        image.setAttribute("crossOrigin", "anonymous");
        image.src = url;
    });
    const [currentFile, setCurrentFile] = useState(null);
    const getCroppedImage = async (imageSrc, pixelCrop, rotation = 0) => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            throw new Error("No 2d context");
        }
        const maxSize = Math.max(pixelCrop.width, pixelCrop.height);
        canvas.width = maxSize;
        canvas.height = maxSize;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const sourceWidth = pixelCrop.width * scaleX;
        const sourceHeight = pixelCrop.height * scaleY;
        const sourceX = pixelCrop.x * scaleX;
        const sourceY = pixelCrop.y * scaleY;
        if (rotation % 180 === 90) {
            canvas.width = maxSize;
            canvas.height = maxSize;
        }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.translate(-canvas.width / 2, -canvas.height / 2);
        const destX = (canvas.width - pixelCrop.width) / 2;
        const destY = (canvas.height - pixelCrop.height) / 2;
        ctx.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, destX, destY, pixelCrop.width, pixelCrop.height);
        const previewCanvas = document.createElement("canvas");
        previewCanvas.width = maxSize;
        previewCanvas.height = maxSize;
        const previewCtx = previewCanvas.getContext("2d");
        if (!previewCtx) {
            throw new Error("No 2d context for preview");
        }
        const centerX = previewCanvas.width / 2;
        const centerY = previewCanvas.height / 2;
        const radius = Math.min(previewCanvas.width, previewCanvas.height) / 2;
        previewCtx.beginPath();
        previewCtx.arc(centerX, centerY, radius, 0, 2 * Math.PI, true);
        previewCtx.closePath();
        previewCtx.clip();
        previewCtx.drawImage(canvas, 0, 0);
        const previewUrl = previewCanvas.toDataURL("image/jpeg", 0.95);
        return new Promise((resolve, reject) => {
            canvas.toBlob(async (blob) => {
                if (!blob) {
                    reject(new Error("Canvas is empty"));
                    return;
                }
                try {
                    const savedFileName = fileName || (currentFile ? currentFile.name : "image.jpg");
                    const fileType = currentFile
                        ? currentFile.type.toLowerCase()
                        : "image/jpeg";
                    const compressedFile = await imageCompression(new File([blob], savedFileName, { type: fileType }), {
                        maxSizeMB: 2,
                        maxWidthOrHeight: maxWidthOrHeight,
                        useWebWorker: false,
                    });
                    const finalFile = new File([compressedFile], savedFileName, {
                        type: compressedFile.type,
                    });
                    resolve({
                        file: finalFile,
                        previewUrl,
                    });
                }
                catch (err) {
                    reject(err);
                }
            }, "image/jpeg", 0.95);
        });
    };
    const handleSave = async () => {
        try {
            if (imageUrl && croppedAreaPixels) {
                setIsSaving(true);
                const { file, previewUrl } = await getCroppedImage(imageUrl, croppedAreaPixels, rotation);
                const savedFileName = fileName || file.name;
                const fileType = file.type;
                await onSave(file, previewUrl, savedFileName, fileType);
                setDisplayedImageUrl(previewUrl);
                setIsUploading(false);
                setShowUploader(false);
            }
        }
        catch (err) {
            console.error("Error saving image:", err);
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleRemove = async () => {
        try {
            setIsRemoving(true);
            await onRemove();
            setImageUrl(null);
            setDisplayedImageUrl(null);
            setIsUploading(false);
            setShowUploader(false);
        }
        catch (err) {
            console.error("Error removing image:", err);
        }
        finally {
            setIsRemoving(false);
        }
    };
    if (displayedImageUrl && !isUploading && !showUploader) {
        return (_jsx("div", { className: "w-full", children: _jsxs("div", { className: "flex flex-col items-center", children: [_jsx(Avatar, { className: "w-[300px] h-[300px] rounded-full overflow-hidden mb-4", children: _jsx(AvatarImage, { src: displayedImageUrl, className: "w-full h-full object-cover" }) }), _jsx("div", { className: "w-full flex justify-end items-center mt-10", children: _jsxs("div", { className: "flex items-center gap-4", children: [!isRemoving && (_jsx(ClickableText, { "data-testid": "removeProfilePictureButton", onClick: handleRemove, children: t("Remove Profile Picture") })), _jsxs(Button, { "data-testid": "changeProfileButton", onClick: () => setShowUploader(true), disabled: isRemoving, children: [t("Change Profile Picture"), " ", isRemoving && _jsx(Spinner, {})] })] }) })] }) }));
    }
    if (isUploading && imageUrl) {
        return (_jsxs("div", { className: "w-full", children: [_jsx("div", { className: "w-full min-w-[300px] h-[344px] bg-[rgb(31,45,55)] rounded-xl relative mx-auto", children: _jsx("div", { className: "h-full rounded-xl overflow-hidden", children: _jsx("div", { className: "relative w-full h-full", children: _jsx(Cropper, { image: imageUrl, crop: crop, cropShape: "round", zoom: zoom, rotation: rotation, aspect: 1 / 1, onCropChange: setCrop, onCropComplete: onCropComplete, onZoomChange: setZoom, onRotationChange: setRotation }) }) }) }), _jsx("div", { className: "w-full flex flex-row my-[3.2rem]", children: _jsxs("div", { className: "w-full", children: [_jsx("div", { className: "flex justify-between items-center text-[rgb(31,41,55)] font-bold text-base", children: _jsx("p", { className: "m-0 leading-normal", children: "Zoom" }) }), _jsxs("div", { className: "flex justify-center items-center my-6 gap-2", children: [_jsx("span", { className: "flex items-center justify-center w-4 h-4", children: _jsx(MinusIcon, {}) }), _jsxs(Slider.Root, { "data-testid": "slider", className: "relative flex items-center w-full touch-none select-none", value: [zoom], min: 1, max: 3, step: 0.1, "aria-labelledby": "Zoom", onValueChange: (newZoom) => setZoom(newZoom[0]), children: [_jsx(Slider.Track, { className: "relative h-2 w-full grow overflow-hidden rounded-full bg-[#DDDDDD]", children: _jsx(Slider.Range, { className: "absolute h-full bg-[#42B4FF]" }) }), _jsx(Slider.Thumb, { "date-testid": "sliderThumb", className: "block h-4 w-4 rounded-full bg-[#42B4FF] transition-colors hover:bg-[#3a9fe6] focus:outline-none focus:ring-2 focus:ring-[#42B4FF] focus:ring-offset-2" })] }), _jsx("span", { className: "flex items-center justify-center w-4 h-4", children: _jsx(PlusIcon, {}) })] })] }) }), _jsx("div", { className: "w-full flex justify-end items-center mt-10", children: _jsxs("div", { className: "flex items-center gap-4", children: [!isSaving && (_jsx(ClickableText, { "data-testid": "cancelButton", onClick: onCancel, children: t("Cancel") })), _jsxs(Button, { "data-testid": "saveButton", onClick: handleSave, disabled: isSaving, children: [t("Save"), " ", isSaving && _jsx(Spinner, {})] })] }) })] }));
    }
    return (_jsxs("div", { className: "w-full", children: [_jsxs("div", { ...getRootProps(), className: "h-[285px] mx-auto rounded-xl border border-dashed border-[#ccccd1] flex flex-col items-center justify-center cursor-pointer bg-[#f3f3f6] hover:bg-[#e5e7eb] transition-all duration-200", children: [_jsx("input", { ...getInputProps(), "data-testid": "fileDragZone" }), _jsx(UploadICon, {}), _jsx("h3", { className: "text-2xl font-bold leading-normal my-6", children: t("Drag and drop") }), _jsxs("p", { className: "mt-4 text-base font-bold leading-normal", children: [t("or"), " ", _jsx("span", { className: "text-[#006bd6]", children: t("browse files") })] })] }), _jsx("div", { className: "w-full flex justify-end items-center mt-10", children: _jsx("div", { className: "flex items-center gap-4", children: _jsx(Button, { "data-testid": "cancelButton", onClick: onCancel, disabled: isRemoving, children: t("Cancel") }) }) })] }));
};
export default ImageUploader;
//# sourceMappingURL=ImageUploader.js.map